'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CompetitiveProofSectionProps {
  currentLanguage?: string;
}

const competitors = [
  { name: 'GenCarbon\n(CAQSG™)', speed: '6–18 months', yield: '2.5–3× higher', tech: 'Quantum-Classical Hybrid + Hyperspectral Drone', mrv: 'Sub-hectare hotspot zoning, 30–40% lower uncertainty', highlight: true },
  { name: 'Varaha', speed: '12–24+ months', yield: 'Competitive', tech: 'Satellite + Basic AI', mrv: 'Conventional remote sensing', highlight: false },
  { name: 'Boomitra', speed: '12–24 months', yield: 'Good for soil', tech: 'Satellite AI', mrv: 'Satellite-based verification', highlight: false },
  { name: 'Mati Carbon', speed: '12–24 months', yield: 'Good for ERW', tech: 'ERW Platform', mrv: 'Photo-based field verification', highlight: false },
  { name: 'EKI Energy', speed: '18–36 months', yield: 'High volume, lower integrity', tech: 'Traditional project dev', mrv: 'Paper-heavy audits', highlight: false },
];

const trustBadges = [
  { name: 'CIMA UK', full: 'Chartered Institute of Management Accountants', color: 'from-blue-600 to-blue-800', icon: '🇬🇧', category: 'Finance & ESG' },
  { name: 'CPA Australia', full: 'CPA Australia — ESG Specialisation', color: 'from-indigo-600 to-indigo-800', icon: '🇦🇺', category: 'ESG Reporting' },
  { name: 'AI & Spatial', full: 'AI & Spatial Intelligence — Advanced Research', color: 'from-sky-700 to-sky-900', icon: '🎓', category: 'AI & Spatial' },
  { name: 'IISc Bengaluru', full: 'Quantum Computing — Indian Institute of Science', color: 'from-emerald-700 to-emerald-900', icon: '⚛️', category: 'Quantum Computing' },
  { name: 'TU Munich', full: 'Aerospace & Space Research — Technical University of Munich', color: 'from-slate-600 to-slate-800', icon: '🚀', category: 'Aerospace Research' },
];

const differentiators = [
  {
    metric: '6–18 months',
    label: 'Credit Timeline',
    vs: 'vs. 12–36 months industry standard',
    icon: 'ClockIcon',
    color: 'emerald',
    description: 'AI/Quantum-powered MRV pipeline delivers credits 2–4× faster than traditional methods',
  },
  {
    metric: '2.5–3×',
    label: 'Higher Carbon Yields',
    vs: 'vs. single-pathway competitors',
    icon: 'ChartBarIcon',
    color: 'amber',
    description: 'Sub-hectare hotspot zoning + layered pathways (ARR + ERW + Circular Waste) multiply yield',
  },
  {
    metric: '30–40%',
    label: 'Lower Uncertainty Buffer',
    vs: 'vs. conventional MRV systems',
    icon: 'ShieldCheckIcon',
    color: 'sky',
    description: 'Reproducible code + peer-reviewed SSRN paper ensures highest scientific transparency',
  },
  {
    metric: '48 hrs',
    label: 'GIS Hotspot Scan',
    vs: 'vs. weeks for competitors',
    icon: 'MapPinIcon',
    color: 'violet',
    description: 'Rapid hyperspectral drone + Graph-Laplacian Spectral Analysis for land assessment',
  },
];

export default function CompetitiveProofSection({ currentLanguage = 'en' }: CompetitiveProofSectionProps) {
  const [showFullTable, setShowFullTable] = useState(false);

  return (
    <section className="py-20 bg-slate-950">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-500/10 rounded-full mb-6 border border-emerald-500/20">
            <Icon name="TrophyIcon" size={16} className="text-emerald-400" />
            <span className="text-sm font-semibold text-emerald-400 uppercase tracking-widest">Why GenCarbon Wins</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            The Only <span className="text-emerald-400">Quantum-Hybrid MRV</span> System in South Asia
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            CAQSG™ — Competitive Advantage through Quantum Spectral Grounding — delivers measurably superior outcomes across every key metric.
          </p>
        </div>

        {/* Key Differentiator Metrics — Bento Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {differentiators.map((d, i) => (
            <div
              key={i}
              className={`relative bg-gradient-to-br from-${d.color}-950/60 to-slate-900 rounded-2xl p-6 border border-${d.color}-500/20 hover:border-${d.color}-400/40 transition-all duration-300 group ${i === 0 ? 'lg:col-span-2 lg:row-span-1' : ''}`}
            >
              <div className={`w-10 h-10 bg-${d.color}-500/20 rounded-xl flex items-center justify-center mb-4`}>
                <Icon name={d.icon as any} size={22} className={`text-${d.color}-400`} />
              </div>
              <div className={`text-3xl font-black text-${d.color}-400 mb-1`}>{d.metric}</div>
              <div className="text-white font-bold text-base mb-1">{d.label}</div>
              <div className={`text-xs text-${d.color}-300/70 font-semibold mb-3`}>{d.vs}</div>
              <p className="text-slate-400 text-xs leading-relaxed">{d.description}</p>
            </div>
          ))}
        </div>

        {/* Trust Signal Badges */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-white mb-2">Academic & Professional Credentials</h3>
            <p className="text-slate-400">Dr. Nupur Mukherjee's multi-disciplinary expertise — the foundation of GenCarbon's competitive moat</p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {trustBadges.map((badge, i) => (
              <div
                key={i}
                className={`group relative bg-gradient-to-br ${badge.color} rounded-2xl px-6 py-4 flex items-center gap-3 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-default min-w-[180px]`}
              >
                <span className="text-2xl">{badge.icon}</span>
                <div>
                  <div className="text-white font-bold text-sm">{badge.name}</div>
                  <div className="text-white/60 text-xs">{badge.category}</div>
                </div>
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 pointer-events-none border border-slate-600">
                  {badge.full}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Competitive Comparison Table */}
        <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 overflow-hidden">
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-white">Competitive Analysis Matrix</h3>
              <p className="text-slate-400 text-sm mt-1">GenCarbon vs. Varaha, Boomitra, Mati Carbon, EKI Energy — May 2026</p>
            </div>
            <button
              onClick={() => setShowFullTable(!showFullTable)}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-xl text-sm font-semibold hover:bg-emerald-500/30 transition-all"
            >
              {showFullTable ? 'Collapse' : 'Expand'}
              <Icon name={showFullTable ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} />
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-6 py-4 text-slate-400 font-semibold w-32">Company</th>
                  <th className="text-left px-4 py-4 text-slate-400 font-semibold">Speed to Credits</th>
                  <th className="text-left px-4 py-4 text-slate-400 font-semibold">Carbon Yield</th>
                  {showFullTable && (
                    <>
                      <th className="text-left px-4 py-4 text-slate-400 font-semibold">Core Technology</th>
                      <th className="text-left px-4 py-4 text-slate-400 font-semibold">MRV Innovation</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {competitors.map((c, i) => (
                  <tr
                    key={i}
                    className={`border-b border-white/5 transition-colors ${c.highlight ? 'bg-emerald-500/10 hover:bg-emerald-500/15' : 'hover:bg-white/5'}`}
                  >
                    <td className="px-6 py-4">
                      <span className={`font-bold text-sm whitespace-pre-line ${c.highlight ? 'text-emerald-400' : 'text-slate-300'}`}>
                        {c.name}
                        {c.highlight && <span className="ml-1 text-xs bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full">★ Best</span>}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`font-semibold ${c.highlight ? 'text-emerald-300' : 'text-slate-400'}`}>{c.speed}</span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`${c.highlight ? 'text-emerald-300 font-semibold' : 'text-slate-400'}`}>{c.yield}</span>
                    </td>
                    {showFullTable && (
                      <>
                        <td className="px-4 py-4 text-slate-400 text-xs max-w-xs">{c.tech}</td>
                        <td className="px-4 py-4 text-slate-400 text-xs max-w-xs">{c.mrv}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CAQSG™ Moat Statement */}
          <div className="p-6 bg-emerald-500/5 border-t border-emerald-500/20">
            <div className="flex items-start gap-3">
              <Icon name="CpuChipIcon" size={20} className="text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-emerald-300 font-bold text-sm mb-1">CAQSG™ Competitive Moat</p>
                <p className="text-slate-400 text-xs leading-relaxed">
                  Patent-pending quantum spectral pipeline + circular waste integration (slag + dairy + fisheries within 200 km radius) + urban segment focus (golf clubs, data centers, IT vendors) — <strong className="text-white">no direct competitor equivalent exists in South Asia.</strong> Backed by SSRN peer-reviewed paper and 70-acre pilot.
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
